import { useState, useEffect } from 'react'
import { useSignIn } from '@clerk/clerk-react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { ChevronLeft, ArrowRight, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BrainLogo } from '@/components/ui/brain-logo'

export default function ResetPasswordPage() {
  const { signIn, setActive, isLoaded } = useSignIn()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Get the token from URL query params (if Clerk redirects with it)
  const token = searchParams.get('token') || searchParams.get('__clerk_ticket')

  useEffect(() => {
    // Check if signIn is ready and has a supported first factor
    if (isLoaded && signIn) {
      const supportedFirstFactors = signIn.supportedFirstFactors || []
      const resetPasswordFactor = supportedFirstFactors.find(
        (factor) => factor.strategy === 'reset_password_email_code'
      )
      
      // If no active reset session and no token, show helpful message
      if (!resetPasswordFactor && !token) {
        // Don't show error immediately - user might be coming from forgot password page
        // They can still enter code if they have it
      }
    }
  }, [isLoaded, signIn, token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoaded || !signIn) return

    if (!code) {
      setError('Please enter the verification code sent to your email')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Attempt to reset the password using the code
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: code,
        password,
      })

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        setSuccess(true)
        // Redirect to onboarding after a short delay
        setTimeout(() => {
          navigate('/onboarding')
        }, 2000)
      } else {
        setError('Password reset failed. Please try again or request a new code.')
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.long_message || 'Failed to reset password. The code may be invalid or expired. Please request a new one.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Section - Branding */}
      <div className="hidden lg:flex relative w-[504px] bg-[#2b42a3] overflow-hidden justify-center">
        <div className="absolute inset-0 bg-[#414fbf] opacity-90" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="mb-8">
              <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm">
                <ChevronLeft className="h-4 w-4" />
                <span>Return Home</span>
              </Link>
            </div>
            
            <div className="mb-8">
              <BrainLogo size="lg" variant="dark" showGlow={false} />
            </div>
            
            <div className="mb-6">
              <h1 className="text-[39px] font-black leading-[47px] mb-4">
                Create New Password
              </h1>
              <p className="text-[#bdbdbd] text-base leading-[28px]">
                Enter your new password below. Make sure it&apos;s strong and secure.
              </p>
            </div>
          </div>
        </div>
        <img
          src="/sign_in_image.png"
          alt="Sign in illustration"
          className="absolute bottom-0 left-0 right-0 w-full h-auto object-contain"
        />
      </div>

      {/* Right Section - Form */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm">
            <ChevronLeft className="h-4 w-4" />
            <span>Return Home</span>
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center p-8 pb-0 lg:p-12 lg:pb-0 bg-[#F8FAFC]">
          <div className="w-full max-w-[460px]">
            <div className="border border-slate-200 rounded-[40px] p-6">
              <div className="rounded-[40px] p-6 -m-1">
                {/* Header */}
                <div className="mb-8">
                  <p className="text-[12.8px] font-normal text-slate-500 mb-2">RESET PASSWORD</p>
                  <h2 className="text-[25px] font-medium leading-[44px]">Set New Password</h2>
                  <p className="text-sm text-slate-600 mt-2">
                    Enter the verification code from your email and create a new password. Your password must be at least 8 characters long.
                  </p>
                </div>

                {success ? (
                  <div className="space-y-6">
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <Lock className="w-8 h-8 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">Password Reset Successful!</h3>
                        <p className="text-sm text-slate-600">
                          Your password has been reset successfully. Redirecting you now...
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Verification Code */}
                    <div className="space-y-3">
                      <Label htmlFor="reset-code" className="text-base text-[#303030]">
                        Verification Code<span className="text-red-500"> *</span>
                      </Label>
                      <Input
                        id="reset-code"
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Enter the code from your email"
                        required={!token}
                        className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 text-base"
                      />
                      <p className="text-xs text-slate-500">
                        Check your email for the verification code sent after requesting a password reset
                      </p>
                    </div>

                    {/* New Password */}
                    <div className="space-y-3">
                      <Label htmlFor="reset-password" className="text-base text-[#303030]">
                        New Password<span className="text-red-500"> *</span>
                      </Label>
                      <Input
                        id="reset-password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your new password"
                        required
                        minLength={8}
                        className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 text-base"
                      />
                      <p className="text-xs text-slate-500">
                        Must be at least 8 characters long
                      </p>
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-3">
                      <Label htmlFor="confirm-password" className="text-base text-[#303030]">
                        Confirm Password<span className="text-red-500"> *</span>
                      </Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your new password"
                        required
                        minLength={8}
                        className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 text-base"
                      />
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                        {error}
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isLoading || !code}
                      className="w-full h-[54px] rounded-[50px] bg-[#4669ed] hover:bg-[#2b42a3] text-white font-bold text-base mt-6"
                    >
                      {isLoading ? 'Resetting Password...' : 'Reset Password'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                )}

                {/* Sign In Link */}
                {!success && (
                  <p className="text-center text-[12.8px] text-[#212121] mt-6">
                    <span className="font-normal">Remember your password? </span>
                    <Link to="/sign-in" className="font-bold underline">
                      SIGN IN
                    </Link>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

